import { useEffect, useRef, useCallback, useState } from "react";
import { Box } from "rebass/styled-components";
import styled from "styled-components";

import { useAds } from "MVVM/Providers";
import CloseButton from "components/atoms/form/button/close";

interface EndSlateAdProps {
  slotId: string;
  game?: string;
  onAdLoaded?: (hasAd: boolean) => void;
  onDismiss?: () => void;
}

const EndSlateAd = ({
  slotId,
  game,
  onAdLoaded,
  onDismiss,
}: EndSlateAdProps) => {
  const { gpt, gptStatus, config } = useAds();
  const slotRef = useRef<typeof window.googletag.Slot | null>(null);
  const isInitializingRef = useRef(false);
  const listenerAddedRef = useRef(false);
  const [domReady, setDomReady] = useState(false);
  const [isLargeAd, setIsLargeAd] = useState(false);
  const [hasAd, setHasAd] = useState(false);

  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setDomReady(true);
    }
  }, []);

  useEffect(() => {
    const displayConfig = config?.display;
    const isEnabled = displayConfig?.enabled === true;

    if (!gpt || gptStatus !== "ready") {
      onAdLoaded?.(false);
      return;
    }

    if (!displayConfig || !isEnabled) {
      onAdLoaded?.(false);
      return;
    }

    if (!domReady) {
      return;
    }

    if (slotRef.current || isInitializingRef.current) {
      return;
    }

    isInitializingRef.current = true;

    gpt.cmd.push(() => {
      if (slotRef.current) {
        isInitializingRef.current = false;
        return;
      }

      const adElement = document.getElementById(slotId);
      if (!adElement) {
        isInitializingRef.current = false;
        onAdLoaded?.(false);
        return;
      }

      const sizeMapping = gpt
        .sizeMapping()
        .addSize([1500, 0], [[800, 450]])
        .addSize([0, 0], [[250, 141]])
        .build();

      const slot = gpt.defineSlot(
        `/${displayConfig.networkId}/${displayConfig.slateAdUnit}`,
        [
          [800, 450],
          [250, 141],
          [300, 168],
        ],
        slotId,
      );

      if (!slot) {
        isInitializingRef.current = false;
        onAdLoaded?.(false);
        return;
      }

      slot.defineSizeMapping(sizeMapping);

      const params = new URLSearchParams(window.location.search);
      const testAds = params.get("testAds");
      if (testAds) {
        slot.setTargeting("test", testAds);
      }

      if (game) {
        slot.setTargeting("game", game);
      }

      const viewerSource = window.location.href.includes("iframe")
        ? "iframe"
        : "website";
      slot.setTargeting("viewer_source", viewerSource);

      slot.addService(gpt.pubads());
      slotRef.current = slot;

      if (!listenerAddedRef.current) {
        listenerAddedRef.current = true;
        gpt
          .pubads()
          .addEventListener(
            "slotRenderEnded",
            (event: {
              slot: typeof slot;
              isEmpty: boolean;
              size: number[] | string;
            }) => {
              if (event.slot === slotRef.current) {
                const adLoaded = !event.isEmpty;
                setHasAd(adLoaded);
                onAdLoaded?.(adLoaded);
                if (
                  adLoaded &&
                  Array.isArray(event.size) &&
                  event.size[0] === 800
                ) {
                  setIsLargeAd(true);
                }
              }
            },
          );
      }

      gpt.display(slotId);
      gpt.pubads().refresh([slot]);
      isInitializingRef.current = false;
    });
  }, [gpt, gptStatus, config?.display, domReady, slotId, game, onAdLoaded]);

  useEffect(() => {
    const currentGpt = gpt;
    return () => {
      if (currentGpt && slotRef.current) {
        const slotToDestroy = slotRef.current;
        currentGpt.cmd.push(() => {
          currentGpt.destroySlots([slotToDestroy]);
        });
        slotRef.current = null;
      }
    };
  }, [gpt]);

  return (
    <SlateWrapper $isLargeAd={isLargeAd}>
      {hasAd && onDismiss && (
        <DismissButton
          size="small"
          classification="round"
          onClick={onDismiss}
        />
      )}
      <SlateContainer ref={containerRef} id={slotId} $isLargeAd={isLargeAd} />
    </SlateWrapper>
  );
};

const SlateWrapper = styled(Box)<{ $isLargeAd?: boolean }>`
  position: relative;

  @media (max-width: 1499px) {
    ${({ $isLargeAd }) => $isLargeAd && "display: none;"}
  }
`;

const SlateContainer = styled(Box)<{ $isLargeAd?: boolean }>`
  width: ${({ $isLargeAd }) => ($isLargeAd ? "800px" : "250px")};
  height: ${({ $isLargeAd }) => ($isLargeAd ? "450px" : "141px")};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DismissButton = styled(CloseButton)`
  position: absolute;
  top: -18px;
  right: -18px;
  z-index: 5;
  transform: scale(0.75);

  :hover {
    background: ${({ theme }) => theme.colors.darkerNed};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export { EndSlateAd };
